import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ApiClientError } from '@/lib/api-client';

interface PurgeMasterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Human label of the row being removed, shown in the prompt. */
  name: string;
  isPending: boolean;
  error: ApiClientError | null;
  onConfirm: () => void;
}

/**
 * Confirmation for permanently deleting a deactivated master row, shared by the
 * warehouse / package-type / facility / supplier lists.
 *
 * The backend only deletes rows nothing references, so the interesting case is
 * the refusal: a 409 means operational records still point at the row and it
 * has to stay deactivated. That is surfaced here rather than silently failing.
 */
export const PurgeMasterDialog = ({
  open,
  onOpenChange,
  name,
  isPending,
  error,
  onConfirm,
}: PurgeMasterDialogProps) => {
  const { t } = useTranslation('masters');

  const message =
    error?.status === 409
      ? error.message ||
        t('efs.masters.purge.referenced', {
          defaultValue:
            'このデータは既存の記録から参照されているため削除できません。無効のままご利用ください。',
        })
      : error
        ? error.message
        : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('efs.masters.purge.title', { defaultValue: '完全に削除しますか？' })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.masters.purge.description', {
              defaultValue:
                '「{{name}}」を完全に削除します。この操作は取り消せません。参照されている記録がある場合は削除できません。',
              name,
            })}
          </DialogDescription>
        </DialogHeader>

        {message ? (
          <Alert variant="destructive">
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={isPending}>
            {isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t('efs.masters.purge.confirm', { defaultValue: '完全に削除' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
